import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";
import { client } from "./client";

export type DirectScope = {
  workspaceId: null;
  merchantId: string;
  param: { merchantId: string };
};

export type WorkspaceScope = {
  workspaceId: string;
  merchantId: string;
  param: { workspaceId: string; merchantId: string };
};

/** Discriminated union — narrow on `workspaceId` to get the right `param` type. */
export type MerchantScope = DirectScope | WorkspaceScope;

/**
 * React hook — returns scope from the current route params.
 * Use in query hooks that run inside a merchant or workspace-merchant page.
 */
export const useScopedMerchantClient = (): MerchantScope => {
  const merchantId = useMerchant();
  const workspaceId = useWorkspaceId();
  return workspaceId
    ? { workspaceId, merchantId, param: { workspaceId, merchantId } }
    : { workspaceId: null, merchantId, param: { merchantId } };
};

/**
 * Non-hook variant for imperative / callback call sites that already have
 * merchantId and workspaceId in scope.
 */
export const getScopedMerchantClient = (merchantId: string, workspaceId: string | null): MerchantScope =>
  workspaceId
    ? { workspaceId, merchantId, param: { workspaceId, merchantId } }
    : { workspaceId: null, merchantId, param: { merchantId } };

/**
 * Call the correct merchant-scoped client endpoint based on a scope object.
 *
 * Usage:
 *   const scope = useScopedMerchantClient();
 *   const res = scope.workspaceId
 *     ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.creators.$get({ param: scope.param, query })
 *     : await client.merchant[":merchantId"].explorer.creators.$get({ param: scope.param, query });
 *
 * Re-export `client` here as a convenience so hooks only import from this file.
 */
export { client };
